/**
 * Korean Grammar Check Core Service
 * Platform-independent implementation for Svelte application
 */

import type {
  SpellCheckResult,
  Correction,
  GrammarCheckSettings,
  AIAnalysisRequest,
  AIAnalysisResult,
  BatchProgress,
  CorrectionStateInfo,
  GrammarCheckError,
  GrammarCheckErrorType
} from '../types/grammar';
import { SpellCheckApiService } from './SpellCheckApiService';
import { cleanSmartPunctuation } from '../utils/textProcessing';
import { CorrectionDeduplicator } from './CorrectionDeduplicator';
import { LRUCache } from '../utils/cache';

/**
 * Core Korean grammar checking service
 * Handles text processing, API integration, and result management
 */
export class KoreanGrammarCore {
  private spellCheckService: SpellCheckApiService;
  private deduplicator: CorrectionDeduplicator;
  private cache: LRUCache<string, SpellCheckResult>;
  private settings: GrammarCheckSettings;
  private isProcessing: boolean = false;

  constructor(settings: GrammarCheckSettings) {
    this.settings = settings;
    this.spellCheckService = new SpellCheckApiService();
    this.deduplicator = new CorrectionDeduplicator();
    
    // Initialize cache with settings
    const cacheConfig = settings.cacheConfig || {
      enabled: true,
      maxEntries: 100,
      ttl: 3600000 // 1 hour default TTL
    };
    
    this.cache = new LRUCache<string, SpellCheckResult>(
      cacheConfig.maxEntries,
      cacheConfig.ttl
    );
  }

  /**
   * Perform spell check on the provided text
   * @param text - Text to check
   * @param progressCallback - Optional callback for progress updates
   * @returns Spell check result
   */
  async performSpellCheck(
    text: string,
    progressCallback?: (progress: BatchProgress) => void
  ): Promise<SpellCheckResult> {
    // Prevent duplicate requests
    if (this.isProcessing) {
      throw this.createError(
        'INVALID_INPUT' as GrammarCheckErrorType,
        'Another spell check is already in progress',
        false
      );
    }

    this.isProcessing = true;

    try {
      // Validate input
      if (!text || !text.trim()) {
        return {
          resultOutput: '',
          corrections: [],
          errorCount: 0,
          processingTime: 0
        };
      }

      // Clean and normalize text
      const cleanedText = cleanSmartPunctuation(text);
      
      // Check cache if enabled
      if (this.settings.cacheConfig?.enabled) {
        const cacheKey = this.generateCacheKey(cleanedText);
        const cachedResult = this.cache.get(cacheKey);
        
        if (cachedResult) {
          return cachedResult;
        }
      }

      const startTime = Date.now();

      // Perform spell check via API service
      const result = await this.spellCheckService.checkSpelling(
        cleanedText,
        this.settings,
        progressCallback
      );

      // Filter single character errors if configured
      if (this.settings.filterSingleCharErrors) {
        result.corrections = result.corrections.filter(
          c => c.original.length > 1
        );
      }

      // Filter ignored words
      if (this.settings.ignoredWords && this.settings.ignoredWords.length > 0) {
        result.corrections = result.corrections.filter(
          c => !this.settings.ignoredWords.includes(c.original)
        );
      }

      // Deduplicate corrections
      result.corrections = this.deduplicator.removeDuplicates(result.corrections);

      // Calculate error positions in original text
      result.corrections = this.calculateErrorPositions(text, result.corrections);

      // Add metadata
      result.errorCount = result.corrections.length;
      result.processingTime = Date.now() - startTime;

      // Cache the result if enabled
      if (this.settings.cacheConfig?.enabled) {
        const cacheKey = this.generateCacheKey(cleanedText);
        this.cache.set(cacheKey, result);
      }

      return result;

    } catch (error) {
      console.error('[KoreanGrammarCore] Spell check error:', error);
      
      if (this.isGrammarCheckError(error)) {
        throw error;
      }
      
      throw this.createError(
        'UNKNOWN' as GrammarCheckErrorType,
        error instanceof Error ? error.message : 'Unknown error occurred',
        false
      );
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Perform AI analysis on corrections
   * @param request - AI analysis request
   * @returns AI analysis results
   */
  async performAIAnalysis(request: AIAnalysisRequest): Promise<AIAnalysisResult[]> {
    if (!this.settings.ai.enabled) {
      throw this.createError(
        'INVALID_INPUT' as GrammarCheckErrorType,
        'AI features are not enabled',
        false
      );
    }

    // This would integrate with AI service
    // For now, returning placeholder
    return [];
  }

  /**
   * Apply corrections to text
   * @param originalText - Original text
   * @param corrections - Corrections to apply
   * @param selectedCorrections - User's selected corrections
   * @returns Corrected text
   */
  applyCorrections(
    originalText: string,
    corrections: Correction[],
    selectedCorrections: Record<number, CorrectionStateInfo>
  ): string {
    let resultText = originalText;
    const replacements: Array<{
      start: number;
      end: number;
      replacement: string;
    }> = [];

    // Process each correction
    corrections.forEach((correction, index) => {
      const selectedState = selectedCorrections[index];
      if (!selectedState) return;

      // Skip if keeping original
      if (selectedState.currentState === 'original-kept' ||
          selectedState.currentState === 'exception-processed') {
        return;
      }

      // Get replacement text
      let replacement = '';
      switch (selectedState.currentState) {
        case 'corrected':
          replacement = selectedState.selectedValue;
          break;
        case 'user-edited':
          replacement = selectedState.selectedValue;
          break;
        default:
          return;
      }

      // Find position if not already calculated
      const position = correction.position || {
        start: originalText.indexOf(correction.original),
        end: originalText.indexOf(correction.original) + correction.original.length
      };

      if (position.start === -1) return;

      replacements.push({
        start: position.start,
        end: position.end,
        replacement
      });
    });

    // Sort replacements in reverse order to maintain positions
    replacements.sort((a, b) => b.start - a.start);

    // Apply replacements
    replacements.forEach(({ start, end, replacement }) => {
      resultText = resultText.slice(0, start) + replacement + resultText.slice(end);
    });

    return resultText;
  }

  /**
   * Add words to the ignored words list
   * @param words - Words to add
   */
  async addIgnoredWords(words: string[]): Promise<void> {
    const newWords = words.filter(
      word => !this.settings.ignoredWords.includes(word)
    );
    
    if (newWords.length > 0) {
      this.settings.ignoredWords.push(...newWords);
      // Clear cache as ignored words have changed
      this.clearCache();
    }
  }

  /**
   * Update settings
   * @param newSettings - New settings
   */
  updateSettings(newSettings: Partial<GrammarCheckSettings>): void {
    this.settings = { ...this.settings, ...newSettings };
    
    // Update cache configuration if changed
    if (newSettings.cacheConfig) {
      const config = newSettings.cacheConfig;
      this.cache = new LRUCache<string, SpellCheckResult>(
        config.maxEntries || 100,
        config.ttl || 3600000
      );
    }
    
    // Clear cache if settings affect results
    if (newSettings.filterSingleCharErrors !== undefined ||
        newSettings.ignoredWords !== undefined ||
        newSettings.apiKey !== undefined ||
        newSettings.apiHost !== undefined) {
      this.clearCache();
    }
  }

  /**
   * Get current settings
   * @returns Current settings
   */
  getSettings(): GrammarCheckSettings {
    return { ...this.settings };
  }

  /**
   * Clear the cache
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   * @returns Cache statistics
   */
  getCacheStats() {
    return this.cache.getStats();
  }

  /**
   * Calculate error positions in the original text
   * @param text - Original text
   * @param corrections - Corrections to calculate positions for
   * @returns Corrections with calculated positions
   */
  private calculateErrorPositions(
    text: string,
    corrections: Correction[]
  ): Correction[] {
    return corrections.map(correction => {
      if (correction.position) {
        return correction;
      }

      const start = text.indexOf(correction.original);
      if (start === -1) {
        // If exact match not found, try to find with normalized text
        const normalizedText = cleanSmartPunctuation(text);
        const normalizedStart = normalizedText.indexOf(correction.original);
        
        if (normalizedStart !== -1) {
          return {
            ...correction,
            position: {
              start: normalizedStart,
              end: normalizedStart + correction.original.length
            }
          };
        }
        
        return correction;
      }

      return {
        ...correction,
        position: {
          start,
          end: start + correction.original.length
        }
      };
    });
  }

  /**
   * Generate cache key for text
   * @param text - Text to generate key for
   * @returns Cache key
   */
  private generateCacheKey(text: string): string {
    // Create a key based on text and relevant settings
    const settingsKey = JSON.stringify({
      filterSingleCharErrors: this.settings.filterSingleCharErrors,
      ignoredWords: this.settings.ignoredWords.sort(),
      apiHost: this.settings.apiHost
    });
    
    // Simple hash function for the key
    const hash = (str: string): string => {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
      }
      return hash.toString(36);
    };
    
    return `${hash(text)}_${hash(settingsKey)}`;
  }

  /**
   * Create a grammar check error
   * @param type - Error type
   * @param message - Error message
   * @param retryable - Whether the error is retryable
   * @returns Grammar check error
   */
  private createError(
    type: GrammarCheckErrorType,
    message: string,
    retryable: boolean
  ): GrammarCheckError {
    return {
      type,
      message,
      retryable
    };
  }

  /**
   * Type guard for GrammarCheckError
   * @param error - Error to check
   * @returns True if error is a GrammarCheckError
   */
  private isGrammarCheckError(error: unknown): error is GrammarCheckError {
    return (
      typeof error === 'object' &&
      error !== null &&
      'type' in error &&
      'message' in error &&
      'retryable' in error
    );
  }
}